import { useId } from 'react';
import { NavLink } from 'react-router-dom';
import { CHECKIN_PROBLEM_LABEL, CountPill, NOTES_TO_REVIEW_LABEL } from '@/components/alarms/CountPill';
import { useCheckinAlertCounts } from '@/hooks/use-checkin-alerts';
import { useNotesReviewCounts } from '@/hooks/use-notes-review';
import { useMe } from '@/hooks/use-me';
import { cn } from '@/lib/cn';
import { groupNavItems, type NavItem } from './nav-items';

/** One sidebar/drawer link: blue tint + blue text when active, no left bar (original). */
/**
 * Sprint 14 (§8.5): the missed check-in badge sits on Oversight → Attendance for Supervisors and on Admin's
 * Projects (Admins reach Attendance through the project tabs).
 */
function useNavBadge(item: NavItem): number {
  const { data: me } = useMe();
  const { total } = useCheckinAlertCounts();
  if (item.key === 'oversight-attendance') return me?.role === 'admin' ? 0 : total;
  if (item.key === 'projects') return me?.role === 'admin' ? total : 0;
  return 0;
}

/** Sprint 15 (§9.1): notes to review on Oversight → Notes (Supervisors; Admins see counts on the project tabs). */
function NotesReviewPill(): JSX.Element | null {
  const { total } = useNotesReviewCounts();
  return <CountPill count={total} label={NOTES_TO_REVIEW_LABEL} tone="neutral" />;
}

export function NavItemLink({ item, onNavigate }: { item: NavItem; onNavigate?: () => void }): JSX.Element {
  const Icon = item.icon;
  const badge = useNavBadge(item);
  return (
    <NavLink
      to={item.path}
      end={item.path === '/'}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors duration-150',
          // text-link is the AA-safe form of the original's text-accent (sprint-02 decision).
          isActive ? 'bg-primary/[0.12] font-medium text-link' : 'text-muted-foreground hover:bg-accent hover:text-foreground',
        )
      }
    >
      <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
      {item.label}
      <CountPill count={badge} label={CHECKIN_PROBLEM_LABEL} />
      {item.key === 'oversight-notes' && <NotesReviewPill />}
    </NavLink>
  );
}

/**
 * Track / Review / Admin groups with their labels (original sidebar). The label ids are prefixed with
 * useId because the sidebar (CSS-hidden below lg) and the open drawer can both be in the DOM.
 */
export function NavGroups({ items, onNavigate }: { items: NavItem[]; onNavigate?: () => void }): JSX.Element {
  const idPrefix = useId();
  return (
    <nav aria-label="Main">
      {groupNavItems(items).map((g, i) => (
        <div key={g.key} className={i > 0 ? 'mt-4' : undefined}>
          <p id={`${idPrefix}nav-group-${g.key}`} className="px-3 pb-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            {g.label}
          </p>
          <ul aria-labelledby={`${idPrefix}nav-group-${g.key}`} className="space-y-0.5">
            {g.items.map((item) => (
              <li key={item.key}>
                <NavItemLink item={item} onNavigate={onNavigate} />
              </li>
            ))}
          </ul>
        </div>
      ))}
    </nav>
  );
}
